import { db } from '../firebase';
import { collection, doc, getDoc, getDocs, addDoc, setDoc, updateDoc, query, where, serverTimestamp, Timestamp } from 'firebase/firestore';
import { InvitationCode, InvitationCodeUsage, InvitationCodeType, InvitationCodeStatus } from '../types/invitation';
import { UserRole } from '../types/userProfile';

export class InvitationService {
  
  /**
   * Determine user role based on invitation code type
   */
  static getUserRoleFromCodeType(codeType: InvitationCodeType): UserRole {
    switch (codeType) {
      case 'master':
        return 'MASTER';
      case 'multi-use':
      case 'single-use':
      default:
        return 'USER';
    }
  }

  /**
   * Generate a random invitation code
   */
  static generateCode(): string {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const segments = 3;
    const segmentLength = 4;
    const code = [];
    
    for (let i = 0; i < segments; i++) {
      let segment = '';
      for (let j = 0; j < segmentLength; j++) {
        segment += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      code.push(segment);
    }
    
    return `TEP-${code.join('-')}`;
  }

  /**
   * Create a new invitation code
   */
  static async createInvitationCode(
    createdBy: string,
    type: InvitationCodeType,
    options?: {
      expiresInDays?: number;
      maxUses?: number;
      restrictedEmail?: string;
      companyId?: string; // NEW: Link code to specific company
      assignedRole?: 'owner' | 'admin' | 'manager' | 'user'; // NEW: Role in company
      notes?: string;
      customCode?: string; // Allow custom code (for master code)
    }
  ): Promise<InvitationCode> {
    try {
      const code = options?.customCode || this.generateCode();
      
      // Check if code already exists
      const existing = await this.getInvitationCodeByCode(code);
      if (existing) {
        throw new Error('Code already exists');
      }

      let expiresAt: Timestamp | undefined;
      if (options?.expiresInDays) {
        const expireDate = new Date();
        expireDate.setDate(expireDate.getDate() + options.expiresInDays);
        expiresAt = Timestamp.fromDate(expireDate);
      }

      const invitationCode: any = {
        code,
        type,
        status: 'active',
        createdBy,
        createdAt: serverTimestamp() as Timestamp,
        usedCount: 0,
        usedBy: [],
      };

      // Only add optional fields if they are defined (not undefined)
      if (expiresAt !== undefined) {
        invitationCode.expiresAt = expiresAt;
      }
      
      if (options?.maxUses !== undefined && options.maxUses !== null) {
        invitationCode.maxUses = options.maxUses;
      }
      
      if (options?.restrictedEmail !== undefined && options.restrictedEmail !== null) {
        invitationCode.restrictedEmail = options.restrictedEmail;
      }
      
      if (options?.companyId !== undefined && options.companyId !== null) {
        invitationCode.companyId = options.companyId;
      }
      
      if (options?.assignedRole !== undefined && options.assignedRole !== null) {
        invitationCode.assignedRole = options.assignedRole;
      }
      
      if (options?.notes !== undefined && options.notes !== null) {
        invitationCode.notes = options.notes;
      }

      const docRef = await addDoc(collection(db, 'invitationCodes'), invitationCode);
      
      return {
        id: docRef.id,
        ...invitationCode,
        createdAt: Timestamp.now(),
      } as InvitationCode;
    } catch (error) {
      console.error('Error creating invitation code:', error);
      throw error;
    }
  }

  /**
   * Get invitation code by code string
   */
  static async getInvitationCodeByCode(code: string): Promise<InvitationCode | null> {
    try {
      const q = query(collection(db, 'invitationCodes'), where('code', '==', code));
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        return null;
      }

      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data()
      } as InvitationCode;
    } catch (error) {
      console.error('Error getting invitation code:', error);
      throw error;
    }
  }

  /**
   * Validate an invitation code
   */
  static async validateCode(code: string, userEmail: string): Promise<{ valid: boolean; error?: string }> {
    try {
      const invitationCode = await this.getInvitationCodeByCode(code);
      
      if (!invitationCode) {
        return { valid: false, error: 'Invalid invitation code' };
      }

      // Check status
      if (invitationCode.status !== 'active') {
        return { valid: false, error: `Code is ${invitationCode.status}` };
      }

      // Check expiration
      if (invitationCode.expiresAt && invitationCode.expiresAt.toDate() < new Date()) {
        // Auto-update status to expired
        await updateDoc(doc(db, 'invitationCodes', invitationCode.id), { status: 'expired' });
        return { valid: false, error: 'Code has expired' };
      }

      // Check email restriction
      if (invitationCode.restrictedEmail && invitationCode.restrictedEmail !== userEmail) {
        return { valid: false, error: 'This code is restricted to a different email address' };
      }

      // Check usage limits
      if (invitationCode.type === 'single-use' && invitationCode.usedCount > 0) {
        return { valid: false, error: 'Code has already been used' };
      }

      if (invitationCode.maxUses && invitationCode.usedCount >= invitationCode.maxUses) {
        return { valid: false, error: 'Code has reached maximum uses' };
      }

      // Check if user already used this code
      if (invitationCode.usedBy && invitationCode.usedBy.includes(userEmail)) {
        return { valid: false, error: 'You have already used this code' };
      }

      return { valid: true };
    } catch (error) {
      console.error('Error validating invitation code:', error);
      return { valid: false, error: 'Error validating code' };
    }
  }

  /**
   * Mark code as used and create user profile with appropriate role
   */
  static async markInvitationCodeAsUsed(code: string, userId: string, userEmail: string, displayName?: string): Promise<UserRole> {
    try {
      const invitationCode = await this.getInvitationCodeByCode(code);
      
      if (!invitationCode) {
        throw new Error('Invitation code not found');
      }

      const userRole = this.getUserRoleFromCodeType(invitationCode.type);
      console.log('🔍 DEBUG: Invitation code type:', invitationCode.type);
      console.log('🔍 DEBUG: Assigned user role:', userRole);
      console.log('🔍 DEBUG: Code details:', { code: invitationCode.code, type: invitationCode.type });

      const usedBy = invitationCode.usedBy || [];
      usedBy.push(userId);

      const updates: Partial<InvitationCode> = {
        usedCount: invitationCode.usedCount + 1,
        usedBy,
      };

      // Mark as used if single-use
      if (invitationCode.type === 'single-use') {
        updates.status = 'used';
      }

      // Mark as used if reached max uses
      if (invitationCode.maxUses && invitationCode.usedCount + 1 >= invitationCode.maxUses) {
        updates.status = 'used';
      }

      await updateDoc(doc(db, 'invitationCodes', invitationCode.id), updates);

      // Log usage
      await addDoc(collection(db, 'invitationCodeUsage'), {
        codeId: invitationCode.id,
        code: invitationCode.code,
        userId,
        userEmail,
        usedAt: serverTimestamp(),
      });

      // Create user profile with role and nickname
      console.log('🔍 DEBUG: Creating user profile with role:', userRole);
      await this.createUserProfile(userId, userEmail, userRole, displayName);
      console.log('🔍 DEBUG: User profile created successfully');

      // NEW: Auto-join company if code is linked to a company
      if (invitationCode.companyId) {
        await this.addUserToCompany(
          userId, 
          invitationCode.companyId, 
          invitationCode.assignedRole || 'user'
        );
      }

      return userRole;
    } catch (error) {
      console.error('Error using invitation code:', error);
      throw error;
    }
  }

  /**
   * Get user role from invitation code usage history
   */
  static async getUserRoleFromUsageHistory(userId: string): Promise<UserRole | null> {
    try {
      const usageQuery = query(
        collection(db, 'invitationCodeUsage'),
        where('userId', '==', userId)
      );
      
      const usageSnapshot = await getDocs(usageQuery);
      
      if (usageSnapshot.empty) {
        return null;
      }
      
      // Get the most recent usage
      const usageDocs = usageSnapshot.docs.sort((a, b) => {
        const aTime = a.data().usedAt?.toMillis() || 0;
        const bTime = b.data().usedAt?.toMillis() || 0;
        return bTime - aTime; // Most recent first
      });
      
      const mostRecentUsage = usageDocs[0].data();
      const code = mostRecentUsage.code;
      
      // Get the invitation code details
      const invitationCode = await this.getInvitationCodeByCode(code);
      if (invitationCode) {
        const role = this.getUserRoleFromCodeType(invitationCode.type);
        console.log('🔍 DEBUG: Found role from usage history:', role, 'for code:', code);
        return role;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user role from usage history:', error);
      return null;
    }
  }

  /**
   * Create user profile with role
   */
  static async createUserProfile(userId: string, userEmail: string, role: UserRole, displayName?: string): Promise<void> {
    try {
      // Generate globally unique nickname (3 letters + 2 sequential numbers)
      const { NicknameService } = await import('./nicknameService');
      const name = displayName || userEmail.split('@')[0];
      const nickname = await NicknameService.generateUniqueNickname(name, userEmail);

      const userProfile = {
        email: userEmail,
        displayName: displayName || userEmail.split('@')[0],
        nickname: nickname,
        role: role,
        companyMemberships: [],
        createdAt: serverTimestamp(),
      };

      // Use setDoc with userId as document ID (not addDoc which generates random ID)
      await setDoc(doc(db, 'userProfiles', userId), userProfile);
      console.log(`✅ User profile created for ${userEmail} with role ${role} and nickname ${nickname}`);
    } catch (error) {
      console.error('Error creating user profile:', error);
      throw error;
    }
  }

  /**
   * Get user profile by user ID
   */
  static async getUserProfile(userId: string): Promise<any> {
    try {
      const docRef = doc(db, 'userProfiles', userId);
      const docSnap = await getDoc(docRef);
      
      if (!docSnap.exists()) {
        return null;
      }

      return docSnap.data();
    } catch (error) {
      console.error('Error getting user profile:', error);
      throw error;
    }
  }

  /**
   * Add user to company automatically when using invitation code
   */
  static async addUserToCompany(
    userId: string, 
    companyId: string, 
    role: 'owner' | 'admin' | 'manager' | 'user'
  ): Promise<void> {
    try {
      // Import CompanyService to avoid circular dependency
      const { CompanyService } = await import('./companyService');
      
      await CompanyService.addCompanyMember({
        companyId,
        userId,
        role,
        status: 'active',
        invitedBy: 'system',
        invitedAt: new Date().toISOString(),
        joinedAt: new Date().toISOString(),
        permissions: this.getDefaultPermissions(role)
      });
      
      console.log(`✅ User ${userId} added to company ${companyId} as ${role}`);
    } catch (error) {
      console.error('Error adding user to company:', error);
      throw error;
    }
  }

  /**
   * Get default permissions based on role
   */
  static getDefaultPermissions(role: string) {
    switch (role) {
      case 'owner':
      case 'admin':
        return {
          canCreateContracts: true,
          canEditAllContracts: true,
          canDeleteContracts: true,
          canManageUsers: true,
          canManageCompany: true,
          canViewAnalytics: true,
          canExportData: true
        };
      case 'manager':
        return {
          canCreateContracts: true,
          canEditAllContracts: true,
          canDeleteContracts: false,
          canManageUsers: false,
          canManageCompany: false,
          canViewAnalytics: true,
          canExportData: true
        };
      case 'user':
      default:
        return {
          canCreateContracts: true,
          canEditAllContracts: false,
          canDeleteContracts: false,
          canManageUsers: false,
          canManageCompany: false,
          canViewAnalytics: false,
          canExportData: false
        };
    }
  }

  /**
   * Get all invitation codes created by a user
   */
  static async getInvitationCodesByCreator(creatorId: string): Promise<InvitationCode[]> {
    try {
      const q = query(collection(db, 'invitationCodes'), where('createdBy', '==', creatorId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as InvitationCode[];
    } catch (error) {
      console.error('Error getting invitation codes:', error);
      throw error;
    }
  }

  /**
   * Get all invitation codes (admin only)
   */
  static async getAllInvitationCodes(): Promise<InvitationCode[]> {
    try {
      const snapshot = await getDocs(collection(db, 'invitationCodes'));
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as InvitationCode[];
    } catch (error) {
      console.error('Error getting all invitation codes:', error);
      throw error;
    }
  }

  /**
   * Revoke an invitation code
   */
  static async revokeInvitationCode(codeId: string): Promise<void> {
    try {
      await updateDoc(doc(db, 'invitationCodes', codeId), {
        status: 'revoked' as InvitationCodeStatus,
      });
    } catch (error) {
      console.error('Error revoking invitation code:', error);
      throw error;
    }
  }

  /**
   * Get usage statistics for a code
   */
  static async getCodeUsageStats(codeId: string): Promise<InvitationCodeUsage[]> {
    try {
      const q = query(collection(db, 'invitationCodeUsage'), where('codeId', '==', codeId));
      const snapshot = await getDocs(q);
      
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as InvitationCodeUsage[];
    } catch (error) {
      console.error('Error getting code usage stats:', error);
      throw error;
    }
  }
}

